import { useMemo } from "react";
import Twemoji from "react-twemoji";
import { ScopeType } from "sockapi/scope-types";
import { TeamSummaryState } from "sockapi/scope-specs/team-list-spec";
import {
  StateDownloadResult,
  useGlobalScopeStatePosthuntable,
  useTeamScopeState,
} from "stores/ScopeStateStore";
import { useTeamId } from "stores/ServerInteractionStore";
import { Spinner } from "components/Loading";
import { TeamDisplayNamePreloaded } from "components/TeamDisplayName";
import DownloadFailed from "components/DownloadFailed";
import { ShortDate } from "components/dates";
import { PuzzlePageWrapper } from "pages/PuzzlePage";

function teamToSortKey(team: TeamSummaryState): number[] {
  const {
    completionTime = Infinity,
    lastSolveTime = Infinity,
    numSolves,
    teamId,
  } = team;
  return [
    completionTime,
    -numSolves,
    lastSolveTime,
    isNaN(+teamId) ? 0 : +teamId,
  ];
}

function useSortedTeamsPosthuntable(
  includeHidden?: boolean,
  freezeAfterHuntEnd?: boolean,
  hasPosthunt?: boolean
): StateDownloadResult<TeamSummaryState[]> | null {
  const teamsResult = useGlobalScopeStatePosthuntable(
    ScopeType.TEAM_LIST,
    true,
    { hasPosthunt: hasPosthunt ?? false }
  );

  return useMemo(() => {
    if (teamsResult === null) return null;
    if (!teamsResult.success) return teamsResult;
    const teams = teamsResult.state;
    const sortedTeams = Object.values(teams)
      .filter((team) => {
        if (team.isHidden && !(includeHidden ?? false)) return false;
        return true;
      })
      .sort((a, b) => {
        const aKey = teamToSortKey(a);
        const bKey = teamToSortKey(b);
        for (let i = 0; i < aKey.length; i++) {
          if (aKey[i] !== bKey[i]) return aKey[i] - bKey[i];
        }
        return a.displayName.localeCompare(b.displayName);
      });
    return { success: true, state: sortedTeams };
  }, [teamsResult, includeHidden]);
}

export function useSortedTeams(
  includeHidden?: boolean,
  freezeAfterHuntEnd?: boolean
): TeamSummaryState[] | null {
  const result = useSortedTeamsPosthuntable(
    includeHidden,
    freezeAfterHuntEnd,
    false
  );
  if (result === null) return null;
  if (!result.success) throw new Error("should not fail if not posthuntable");
  return result.state;
}

const TeamListPageBody = () => {
  const teamId = useTeamId();
  const teamScopeState = useTeamScopeState();
  const sortedTeamsResult = useSortedTeamsPosthuntable(false, true, true);

  if (teamScopeState === null || sortedTeamsResult === null)
    return <Spinner size={48} />;
  if (!sortedTeamsResult.success)
    return <DownloadFailed {...sortedTeamsResult} />;
  const sortedTeams = sortedTeamsResult.state;

  const teamsDom = sortedTeams.map((team, i) => {
    const { numSolves, completionTime } = team;
    return (
      <tr
        className={`${
          teamId !== null && team.teamId === teamId ? "current-team" : ""
        }`}
        key={team.teamId}
      >
        <td>
          {completionTime ? <Twemoji tag="span">🌏</Twemoji> : null}
          {i + 1}
        </td>
        <td>
          <TeamDisplayNamePreloaded teamData={team} />
        </td>
        <td style={{ whiteSpace: "nowrap", fontVariant: "tabular-nums" }}>
          {numSolves}
          {/*
          {completionTime === undefined ||
          numSolveCountPuzzles === undefined ? null : (
            <small>
              {" "}
              ({Math.floor((numSolves / numSolveCountPuzzles) * 100)}%)
            </small>
          )}
          */}
        </td>
        <td>
          {completionTime ? <ShortDate timestamp={completionTime} /> : null}
        </td>
      </tr>
    );
  });

  return (
    <div className="centered-page">
      <h1>Team Scoreboard</h1>
      <table className="list-table">
        <colgroup>
          <col style={{ width: "10%" }} />
          <col style={{ width: "60%" }} />
          <col style={{ width: "10%" }} />
          <col style={{ width: "20%" }} />
        </colgroup>
        <thead>
          <tr>
            <th>Rank</th>
            <th>Team</th>
            <th>Solves</th>
            <th>Finish time</th>
          </tr>
        </thead>
        <tbody>{teamsDom}</tbody>
      </table>
    </div>
  );
};

function TeamListPage() {
  return (
    <PuzzlePageWrapper>
      <TeamListPageBody />
    </PuzzlePageWrapper>
  );
}

export default TeamListPage;
